import { JstDate } from '../../core/time/business-time';
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { PageTitleService } from '../../core/services/page-title.service';
import { CounselService } from '../../core/services/counsel.service';

declare const imageCompression: (
  file: File,
  options: { maxSizeMB: number; maxWidthOrHeight: number }
) => Promise<File>;

@Component({
    selector: 'app-counsel-item',
    imports: [CommonModule, FormsModule, RouterLink],
    templateUrl: './counsel-item.component.html'
})
export class CounselItemComponent implements OnInit {
  private pageTitle = inject(PageTitleService);
  private counselService = inject(CounselService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  counsel: any = {};
  imgCount = 0;
  myPicture: File[] = [];
  myPictureInfo = '';
  isIncorrectType = false;
  isUploading = false;
  uploadMessage = '';
  uploadUncertain = false;

  async ngOnInit(): Promise<void> {
    const counselId = this.route.snapshot.params['counselId'];
    if (!counselId) { this.router.navigate(['/counsel']); return; }
    this.pageTitle.set('fa s-icon-list', 'カウンセリング詳細');
    this.counsel = await this.counselService.getOne(counselId);
    this.imgCount = [this.counsel.uploadedImg1, this.counsel.uploadedImg2, this.counsel.uploadedImg3, this.counsel.uploadedImg4]
      .filter(Boolean).length;
    this.resetPictureInfo();
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    if (dateStr.endsWith('Z')) {
      const d = new JstDate(dateStr);
      return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}`;
    }
    return dateStr.split('T')[0];
  }

  onFilesChange(event: Event): void {
    if (this.isUploading) return;
    this.uploadMessage = '';
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    // 同じファイルを再選択したときも change を発生させる。
    input.value = '';
    if (files.length > 0) {
      this.myPicture = files;
      this.isIncorrectType = files.some(f => !['image/jpg', 'image/jpeg', 'image/png'].includes(f.type));
      this.myPictureInfo = files[0].name + (files.length > 1 ? `、他${files.length - 1}ファイル` : '');
    } else {
      this.clearMyPicture();
    }
  }

  clearMyPicture(): void {
    this.myPicture = [];
    this.isIncorrectType = false;
    this.resetPictureInfo();
  }

  resetPictureInfo(): void {
    this.myPictureInfo = `${4 - this.imgCount}ファイル登録可能`;
  }

  get canUpload(): boolean {
    return !this.uploadUncertain && this.myPicture.length > 0 && this.myPicture.length <= (4 - this.imgCount) && !this.isIncorrectType;
  }

  async refreshUploadState(): Promise<void> {
    this.uploadUncertain = true;
    try {
      this.counsel = await this.counselService.readUploadState(this.counsel.id);
      this.imgCount = [1, 2, 3, 4].filter(i => this.counsel['uploadedImg' + i]).length;
      this.clearMyPicture();
      this.uploadUncertain = false;
    } catch {
      this.uploadMessage = '画像の保存状態を確認できません。再送せず、保存状態を再取得してください。';
    }
  }

  async upload(): Promise<void> {
    if (!this.canUpload || this.isUploading) return;
    this.isUploading = true;
    this.uploadMessage = '';
    let sent = false;
    let completed = 0;
    try {
      const files = await Promise.all(this.myPicture.map(file => imageCompression(file, { maxSizeMB: 0.8, maxWidthOrHeight: 800 })));
      const current = await this.counselService.readUploadState(this.counsel.id);
      const targetIds = [1, 2, 3, 4].filter(i => !current['uploadedImg' + i]);
      if (files.length > targetIds.length) {
        this.uploadMessage = '登録済み画像が変更されました。保存状態を確認し、ファイルを選び直してください。';
        await this.refreshUploadState();
        return;
      }
      for (let i = 0; i < files.length; i++) {
        const compressed = files[i];
        const formData = new FormData();
        formData.append('file', new File([compressed], compressed.name, { type: compressed.type }));
        sent = true;
        await this.counselService.uploadPicture(this.counsel.id, targetIds[i], formData);
        completed++;
      }
      await this.refreshUploadState();
      if (!this.uploadUncertain) {
        this.uploadMessage = completed + '枚の画像をアップロードしました。';
        this.toastr.success('画像をアップロードしました', '', { timeOut: 5000 });
      }
    } catch {
      if (sent) {
        this.uploadMessage = '画像の送信を完了できませんでした。登録済み画像を確認し、未登録のファイルだけを選び直してください。';
        await this.refreshUploadState();
      } else {
        this.uploadMessage = '画像を準備できませんでした。画像ファイルと通信を確認して、もう一度お試しください。';
      }
    } finally {
      this.isUploading = false;
    }
  }
}
